import types from './types'
import router from '@/router'
import * as arcApiV2 from '@/services/v2/arc-api-v2.service'

import { camelizeKeys, decamelizeKeys } from 'humps'
import { sanitizeEndpointError } from '@/utils/errors.utils'
import { filtersConfiguration } from '@/config/v2/authorizationsV2.js';
import { createAuthorizationActions } from '@/config/v2/authorizationsV2.js';
import { getFieldsByForm } from '@/utils/v2/forms-v2.utils'

const showAlert = (dispatch, message, type = 'error') => {
  dispatch(
    'general/configureAlert',
    {
      active: true,
      type,
      message
    },
    { root: true }
  )
}

export const resetAuthorizationState = ({ commit }) => {
  commit(types.RESET_AUTHORIZATION_STATE)
}

export const getAuthorizationDetailForm = async ({ commit, dispatch }, payload) => {
  try {
    const response = await arcApiV2.getAuthorizationDetailForm(payload.sponsorSlug, payload.formSlug)
    const data = camelizeKeys(response.data)
    commit(types.AUTHORIZATION_DETAIL_FORM, data[0])

  }catch(error) {
    const message = sanitizeEndpointError(error)
    showAlert(dispatch, message)
  }
}

export const getAuthorizationWithoutClaims = async ({ commit }, filters) => {
  try {
    const response = await arcApiV2.getAuthorizationsWithoutClaim(filters);
    commit(types.AUTHORIZATIONS_WITHOUT_CLAIMS_SUCCESS, camelizeKeys(response.data))
  } catch (error) {
    return
  }
}

export const setAccountSearchValue = ({ commit }, payload) => {
  commit(types.SET_ACCOUNTS_SEARCH_VALUE, payload)
}

export const setFiltersConfiguration = async ({ commit }) => {
  const filters = filtersConfiguration;

  commit(types.SET_FILTERS_CONFIGURATION, filters)
}

export const getAuthorizationDetail = async ({ commit, rootState, dispatch }, payload) => {
  try {
    const { providerSlug } = rootState.auth.tokenExtensions

    if(!payload.authorizationCode || !payload.sponsorSlug){
      return
    }

    const response = await arcApiV2.getAuthorizationWithoutClaimDetail({...payload, providerSlug})
    commit(types.AUTHORIZATION_DETAIL, camelizeKeys(response.data))
  } catch (error) {
    const message = sanitizeEndpointError(error)
    showAlert(dispatch, message)
  }
}

export const getAuthorizationsForm = async ({ commit}, payload) => {
  try {
    const response = await arcApiV2.getAuthorizationForm(payload.sponsorSlug, payload.authorizationFormSlug);
    const data = camelizeKeys(response.data)

    const authenticatedForms = data.filter(form => !form.formSlug.includes('guest'))
    const formFields = getFieldsByForm(authenticatedForms)
    const formFieldsKeys = Object.keys(formFields)

    if (formFieldsKeys.length === 1 ) {
      formFields[formFieldsKeys[0]].shift()
      commit(types.SET_CURRENT_FORM, formFieldsKeys[0])
    }

    commit(types.AUTHORIZATION_FORM_LOAD_SUCCESS, { response: data, formsByStep: formFields })
    commit(types.AUTHORIZATION_LOADING, false)

  } catch (error) {
    commit(types.AUTHORIZATION_LOAD_ERROR)
    commit(types.AUTHORIZATION_LOADING, false)
  }
}

export const setCurrentStep = ({ commit }, payload) => {
  commit(types.SET_CURRENT_STEP, payload)
}

export const setStepError = ({ commit }, payload) => {
  commit(types.SET_STEP_ERROR, payload)
}

export const setInput = ({ commit }, payload) => {
  commit(types.SET_INPUTS, payload)
}

export const setError = ({ commit }, payload) => {
  commit(types.SET_ERROR, payload);
}

export const nextStep = ({ commit, state, dispatch, getters }, payload) => {
  dispatch('setError',{hasError: false, error: null})

  if (state.currentStep === payload) {
    const { authorizationForms, currentForm } = getters;
    const form = authorizationForms.find(form => form.formSlug === currentForm)

    if (form && !form.reimbursement) {
      if(getters.createAuthorizationAction === createAuthorizationActions.ADDITIONAL_PRODUCTS){
        dispatch('sendAuthorizationAdditionalProducts')
      }
    }

  } else {
    commit(types.SET_CURRENT_STEP, state.currentStep + 1)
  }
}

export const sendAuthorizationAdditionalProducts = async ({ commit, state, rootState, dispatch }) => {
  commit(types.AUTHORIZATION_LOADING, true)

  try {
    const { inputs, currentForm, authorizationDetail } = state

    const sponsorSlug = router.currentRoute.params.sponsorSlug
    const authorizationCode = router.currentRoute.params.authorizationCode
    const detailFormSlug = authorizationDetail.detailFormSlug

    const providerSlug = rootState.auth.tokenExtensions.providerSlug

    const payload = {
      formSlug: currentForm,
      sponsorSlug,
      providerSlug,
      inputs
    }

    await arcApiV2.sendAuthorizationAdditionalProducts(authorizationCode, decamelizeKeys(payload))

    commit(types.SET_CURRENT_STEP, 1)
    commit(types.RESET_AUTHORIZATION_STATE)


    router.push({
      name: 'v2DetailAuthorization',
      params: {
        sponsorSlug,
        authorizationCode
      },
      query: {
        detailForm: detailFormSlug
      }
    })

    commit(types.AUTHORIZATION_LOADING, false)

  }catch(error){
    commit(types.AUTHORIZATION_LOADING, false)
    const message = sanitizeEndpointError(error)
    showAlert(dispatch, message)
  }
}

export const sendReimbursementAdditionalProducts = async ({ commit, state, dispatch }) => {
  commit(types.AUTHORIZATION_LOADING, true)

  try{
    const {inputs, currentForm} = state
    const {authorization} = state

    if(!authorization){
      commit(types.SET_ERROR, {
        hasError: true,
        error: 'Error al obtener la autorización creada, porfavor intente nuevamente'
      })

      return;
    }

    const {reimbursementId} = authorization

    const payload = {
      formSlug: currentForm,
      inputs
    }

    const { reimbursementAdditionalFiles } = state
    payload['additionalFiles'] = reimbursementAdditionalFiles

    await arcApiV2.sendReimbursementAdditionalProducts(reimbursementId, decamelizeKeys(payload))

    commit(types.SET_CURRENT_STEP, 1)
    commit(types.RESET_AUTHORIZATION_STATE)
    dispatch('getAuthorizationDetail', authorization.authorizationCode)

  }finally{
    commit(types.AUTHORIZATION_LOADING, false)
  }
}

export const setCreateAuthorizationAction = ({ commit }, payload) => {
  commit(types.SET_CREATE_AUTHORIZATION_ACTION, payload)
}

export const setSponsorStaticDocuments = async ({ commit }, payload) => {
  try {
    const response = await arcApiV2.getAuthorizationSponsorStaticDocuments(payload.sponsorSlug, payload.coverageTypes)
    commit(types.SET_AUTHORIZATION_SPONSOR_STATIC_DOCUMENTS, camelizeKeys(response.data))
  } catch (error) {
    const message = sanitizeEndpointError(error)
    commit(types.SET_AUTHORIZATION_SPONSOR_STATIC_DOCUMENTS_ERROR, message)
  }
}

export const setAuthorizationSponsorStaticDocument = async ({commit}, sponsorSlug, fileID) => {
  try {
      const response = await arcApiV2.getAuthorizationSponsorStaticDocument(sponsorSlug, fileID);
      commit(types.AUTHORIZATION_DETAIL_V2_FILE, camelizeKeys(response.data))
  } catch (error) {
      commit(types.AUTHORIZATION_DETAIL_V2_FILE_ERROR, error)
  }
}

export const clearAuthorizationDetail = ({ commit }) => {
  commit(types.AUTHORIZATION_DETAIL, {})
}

export const sendBeneficiaryComment = async ({ state, dispatch }, comment) => {
  const payload = decamelizeKeys({
    comment,
    privateComment: false,
    beneficiaryComment: true
  })

  const authorizationCode = state.authorizationDetail.authorizationCode
  const authorizationId = state.authorizationDetail.authorizationId
  const sponsorSlug = state.authorizationDetail.sponsorSlug

  try {
    await arcApiV2.sendComment(authorizationCode, authorizationId, payload)
    await dispatch('getAuthorizationDetail', { sponsorSlug, authorizationCode })

  }catch(error){
    const message = sanitizeEndpointError(error)
    showAlert(dispatch, message)
  }
}

export const setAuthorizationPreInvoice = ({commit}, payload) => {
  commit(types.SET_AUTHORIZATION_PRE_INVOICE, payload)
}

export const clearAuthorizationPreInvoice = ({commit}, payload) => {
  commit(types.CLEAR_AUTHORIZATION_PRE_INVOICE, payload)
}

export const setNewAuthorizationStatus = async ({ dispatch, commit}, payload) => {

  try {
    const response = await arcApiV2.changeStatus(payload.sponsorSlug, payload.authorizationCode, payload.status);

    commit(types.SET_NEW_AUTHORIZATION_STATUS, camelizeKeys(response.data));
  } catch (error) {
    const message = sanitizeEndpointError(error)
    showAlert(dispatch, message)
  } 
}

export const clearAuthorizationAccountInfo = ({ commit }) => {
  commit(types.SET_ACCOUNT_INFO, undefined)
}

export const uploadAuthorizationFile = async (_props, {
  file,
  authorizationId,
  fieldSlug,
  authorizationFormFieldId,
  documentTypeSlug,
  authorizationFileId
}) => {
  const formData = new FormData()
  formData.append('file', file)

  const payload = decamelizeKeys({
    fieldSlug,
    authorizationFormFieldId,
    documentTypeSlug,
    authorizationFileId
  })

  const payloadString = JSON.stringify(payload)
  const payloadBlob = new Blob([payloadString], { type: 'application/json' })

  formData.append('request_data', payloadBlob)

  return await arcApiV2.uploadAuthorizationFile({authorizationId, formData})
}

export const downloadAuthorizationFile = async ({ dispatch }, authorizationFileId) => {
  try {
    return await arcApiV2.downloadAuthorizationFile(authorizationFileId)
  }catch(error){
    const message = sanitizeEndpointError(error)
    showAlert(dispatch, message)
  }
}

export const deleteAuthorizationFile = async ({ dispatch }, { authorizationId, authorizationFileId }) => {
  try {
    await arcApiV2.deleteAuthorizationFile(authorizationId, authorizationFileId)
  }catch(error){
    const message = sanitizeEndpointError(error)
    showAlert(dispatch, message)
  }
}

export const setAuthorizationInvoice = ({commit}, payload) => {
  commit(types.SET_AUTHORIZATION_INVOICE, payload)
}

export const clearAuthorizationInvoice = ({commit}, payload) => {
  commit(types.CLEAR_AUTHORIZATION_INVOICE, payload)
}

export const deleteInvoiceFile = ({commit}, payload) => {
  commit(types.DELETE_AUTHORIZATION_INVOICE, payload)
}
